// THE SHELL'S OWN VIEWS IN AN AI ANSWER — the team and its agents, the
// notifications, the reminders (workspace functions/assistantAppViews.js, the
// `views[]` contract; Nika 2026-10-18: "fly should cover all functionality
// that would be possible otherwise"). Every workspace has them — they are the
// shell's, never an installed app's:
//
//   team-people     the workspace's members (who is online now) and its AI
//                   agents — the roster the shell keeps (root.people, the
//                   presence list root.onlineUsers) and the org's agents
//                   (root._agents, else the same sdk.listAgents read). A
//                   member opens their profile; an agent, Team & agents.
//   notifications   the bell's list (listNotifications), unread first; a row
//                   opens what the bell row opens (notificationHrefOf +
//                   openNotificationTarget). Nothing is marked read here.
//   reminders       the reminders app's own groups (reminderGroups: overdue,
//                   today, tomorrow, upcoming, no date, completed) over the
//                   records plane's `reminders` rows, the app's own scope and
//                   filter rules; a row opens Reminders.
//
// Each view is the shared list frame (components/AssistantViewList.js): a
// read into the frame's OWN state, a model of labelled HomeWidgetRow groups.
// Nothing here writes.
//
// Frank note: el.call serializes each export on its own (FA204) — every export
// is self-contained.

export function workspaceAiViews() {
  return {
    app: {
      id: 'system/workspace',
      navbar: [{ label: 'shell.app.workspace', route: '/', icon: 'home' }],
      apps: [{ icon: 'home' }]
    },
    fold: null,
    views: [
      {
        id: 'team-people',
        title: 'workspace.teamAnswer.title',
        describe:
          "The workspace's people and its AI agents, and who is online now; show = everyone, online, members or agents; search = a name, email or role.",
        component: 'TeamPeopleAnswerView',
        icon: 'people',
        hue: 'people',
        size: 'l',
        params: {
          show: {
            type: 'enum',
            values: ['all', 'online', 'members', 'agents'],
            labels: {
              all: 'workspace.teamAnswer.show.all',
              online: 'workspace.teamAnswer.show.online',
              members: 'workspace.teamAnswer.show.members',
              agents: 'workspace.teamAnswer.show.agents'
            },
            default: 'all'
          },
          search: { type: 'string', describe: 'words in a name, email or position' },
          limit: { type: 'number', min: 1, max: 50, default: 24 }
        },
        link: { route: '/team-and-agents', query: {} }
      },
      {
        id: 'notifications',
        title: 'workspace.notificationsAnswer.title',
        describe:
          "The member's notifications (the bell), unread first, newest first; show = all, unread, or only this workspace's.",
        component: 'NotificationsAnswerView',
        icon: 'bell',
        hue: 'analytics',
        size: 'l',
        params: {
          show: {
            type: 'enum',
            values: ['all', 'unread', 'workspace'],
            labels: {
              all: 'workspace.notificationsAnswer.show.all',
              unread: 'workspace.notificationsAnswer.show.unread',
              workspace: 'workspace.notificationsAnswer.show.workspace'
            },
            default: 'all'
          },
          limit: { type: 'number', min: 1, max: 50, default: 12 }
        },
        link: { route: '/inbox', query: {} }
      },
      {
        id: 'reminders',
        title: 'workspace.reminders.listTitle',
        describe:
          "The member's reminders by due day (overdue, today, upcoming, no date, completed); filter = all, today, upcoming or completed; scope = private or shared.",
        component: 'RemindersAnswerView',
        icon: 'clockOutline',
        hue: 'orange',
        size: 'l',
        params: {
          filter: {
            type: 'enum',
            values: ['all', 'today', 'upcoming', 'completed'],
            labels: {
              all: 'workspace.reminders.filter.all',
              today: 'workspace.reminders.filter.today',
              upcoming: 'workspace.reminders.filter.upcoming',
              completed: 'workspace.reminders.filter.completed'
            },
            default: 'all'
          },
          scope: {
            type: 'enum',
            values: ['private', 'shared'],
            labels: {
              private: 'workspace.reminders.scope.private',
              shared: 'workspace.reminders.scope.shared'
            },
            default: 'private'
          },
          limit: { type: 'number', min: 1, max: 50, default: 12 }
        },
        link: { route: '/reminders', query: {} },
        access: { route: '/reminders' }
      }
    ]
  }
}

// ── team-people ─────────────────────────────────────────────────────────────

// The read: the org's agents, unless a roster producer already filled the
// shared contract (root._agents); and the members ONLY when the shell's own
// roster never answered (a host where loadPrivateData did not run).
// `{ data: { agents, agentsFailed, people }, failed }` — agents null = read
// root._agents; people null = read root.people.
export async function teamPeopleAnswerLoad(el) {
  const host = el || this
  const root = (host && host.state && host.state.root) || {}
  const sdk = (host && host.sdk) || (host && host.call ? host.call('getSDK') : null)
  const out = { agents: null, agentsFailed: false, people: null }
  const rosterReady =
    root.peopleLoaded === true ||
    !!root.peopleError ||
    (Array.isArray(root.people) && root.people.length > 0)
  const reads = []
  if (!Array.isArray(root._agents)) {
    const orgId = root.jwtClaims && root.jwtClaims.workspace_org_id
    if (sdk && typeof sdk.listAgents === 'function' && orgId) {
      reads.push(
        Promise.resolve()
          .then(() => sdk.listAgents(orgId))
          .then((raw) => {
            const list = Array.isArray(raw) ? raw : (raw && raw.data) || []
            // The roster contract's row shape (the home producer's, pages/
            // main.js _loadHomeAgentRoster): the name per language rides as
            // `nameI18n`, resolved where the row is drawn.
            out.agents = list
              .map((u) => ({
                id: String((u && (u.username || u.id || u._id)) || '').toLowerCase(),
                name: (u && (u.name || u.username)) || '',
                kind: 'agent',
                avatar: (u && u.avatar) || null,
                userId: (u && (u.id || u._id)) || null,
                email: (u && u.email) || null,
                nameI18n: (u && u.metadata && u.metadata.nameI18n) || null
              }))
              .filter((a) => a.userId)
          })
          .catch(() => {
            out.agents = []
            out.agentsFailed = true
          })
      )
    } else {
      out.agents = []
    }
  }
  if (!rosterReady && !sdk) out.people = []
  if (!rosterReady && sdk) {
    const ws = String((root.workspace && (root.workspace.id || root.workspace._id)) || '') || null
    reads.push(
      Promise.resolve()
        .then(() => host.call('listOrgMembers', sdk, ws))
        .then((rows) => {
          out.people = (Array.isArray(rows) ? rows : []).map((r) => ({
            userId: r.user_id,
            email: r.email,
            name: r.name || r.email || '',
            avatar: typeof r.avatar === 'string' && /^(https?:|\/)/.test(r.avatar) ? r.avatar : '',
            initials: host.call('initials', r.name || ''),
            position: r.position,
            status: r.status
          }))
        })
        .catch(() => {
          out.people = []
        })
    )
  }
  await Promise.all(reads)
  const failed = out.agentsFailed && Array.isArray(out.people) && !out.people.length
  return { data: out, failed }
}

// The model: Members (online first) and Agents, or the online members alone.
// The rows follow the root roster and presence as they change.
export function teamPeopleAnswerModel(data, params) {
  const el = this
  const root = (el && el.state && el.state.root) || {}
  const d = data && typeof data === 'object' ? data : {}
  const p = params && typeof params === 'object' ? params : {}
  const say = (key) => String(el.call('polyglot', key))
  const rosterReady =
    root.peopleLoaded === true ||
    !!root.peopleError ||
    (Array.isArray(root.people) && root.people.length > 0)
  if (!rosterReady && !Array.isArray(d.people)) return null
  const show = ['all', 'online', 'members', 'agents'].includes(p.show) ? p.show : 'all'
  const limit = Math.max(1, Math.min(50, Math.floor(Number(p.limit)) || 24))
  const words = String(p.search || '')
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean)
  const matches = (...fields) => {
    const hay = fields
      .filter(Boolean)
      .map((f) => String(f).toLowerCase())
      .join(' ')
    return words.every((w) => hay.includes(w))
  }
  const lower = (v) => String(v || '').toLowerCase()
  const me = lower(root.currentUserEmail)
  const myId = String(root.currentUserId || '')
  const online = new Set(
    (Array.isArray(root.onlineUsers) ? root.onlineUsers : [])
      .map((u) => lower(u && u.email))
      .filter(Boolean)
  )
  const agents = Array.isArray(d.agents)
    ? d.agents
    : Array.isArray(root._agents)
      ? root._agents
      : []
  const agentIds = new Set(agents.map((a) => String((a && a.userId) || '')).filter(Boolean))
  const agentEmails = new Set(agents.map((a) => lower(a && a.email)).filter(Boolean))
  // Team & agents' own roster rule (canvas pages/team-and-agents.js
  // _visiblePeople): the departed and the read-only drop out — the viewer is
  // always kept; an agent's own user row is listed once, as an agent.
  const roster = rosterReady
    ? Array.isArray(root.people)
      ? root.people
      : []
    : Array.isArray(d.people)
      ? d.people
      : []
  const isMe = (x) => (myId && String(x.userId) === myId) || (me && lower(x.email) === me)
  const people = roster.filter(
    (x) =>
      x &&
      (isMe(x) || (x.status !== 'left' && x.status !== 'inactive')) &&
      !agentIds.has(String(x.userId || '')) &&
      !agentEmails.has(lower(x.email))
  )
  const isOnline = (x) => !!x.email && online.has(lower(x.email))
  const memberRow = (x) => {
    const name = x.name || x.email || say('workspace.person.unknown')
    const self = isMe(x)
    return el.call('assistantViewRow', {
      key: 'p-' + String(x.userId || x.email || name).replace(/[^a-zA-Z0-9_-]/g, ''),
      title: name,
      meta: self
        ? say('workspace.teamAnswer.you')
        : isOnline(x)
          ? say('workspace.teamAnswer.online')
          : x.position || '',
      src: x.avatar || null,
      initials: x.avatar ? null : x.initials || el.call('initials', name) || null,
      open: x.email
        ? { call: 'openProfileModal', arg: String(x.email) }
        : { call: 'openAppPage', arg: '/team-members' }
    })
  }
  const agentRow = (a) => {
    const name =
      el.call(
        'agentDisplayName',
        { name: a.name, metadata: { nameI18n: a.nameI18n } },
        root.lang
      ) ||
      a.name ||
      say('workspace.agents.fallbackName')
    return el.call('assistantViewRow', {
      key: 'a-' + String(a.userId || a.id || name).replace(/[^a-zA-Z0-9_-]/g, ''),
      title: name,
      meta: say('workspace.teamAnswer.agent'),
      src: a.avatar || null,
      initials: a.avatar ? null : el.call('initials', name) || null,
      open: { call: 'openAppPage', arg: '/team-and-agents' }
    })
  }
  const byName = (a, b) =>
    String(a.name || a.email || '').localeCompare(String(b.name || b.email || ''))
  const members = people
    .filter((x) => matches(x.name, x.email, x.position))
    .sort((a, b) => Number(isOnline(b)) - Number(isOnline(a)) || byName(a, b))
  const bots = agents.filter((a) => a && matches(a.name, a.email))
  const groups = []
  let shown = 0
  let total = 0
  const put = (key, labelKey, list, toRow) => {
    total += list.length
    const rows = list.slice(0, limit).map(toRow)
    shown += rows.length
    if (rows.length) {
      groups.push(
        el.call('assistantViewGroup', {
          key,
          label: say(labelKey).replace('{count}', String(list.length)),
          rows
        })
      )
    }
  }
  if (show === 'online') {
    put(
      'online',
      'workspace.teamAnswer.group.online',
      members.filter((x) => isOnline(x) && !isMe(x)),
      memberRow
    )
  } else {
    if (show !== 'agents') put('members', 'workspace.teamAnswer.group.members', members, memberRow)
    if (show !== 'members') put('agents', 'workspace.teamAnswer.group.agents', bots, agentRow)
  }
  let note = ''
  if (!groups.length) {
    note =
      show === 'online' && !words.length
        ? 'workspace.teamAnswer.noneOnline'
        : d.agentsFailed && show === 'agents'
          ? 'workspace.teamAnswer.failed'
          : 'workspace.teamAnswer.empty'
  }
  return { groups, note, more: { shown, total } }
}

// ── notifications ───────────────────────────────────────────────────────────

// The bell's own read (shared listNotifications — the recipient is the
// token's), newest first.
export async function notificationsAnswerLoad(el) {
  const host = el || this
  let res = null
  try {
    res = await host.call('listNotifications')
  } catch (_) {
    res = { data: [], error: true }
  }
  const rows = res && Array.isArray(res.data) ? res.data : []
  const items = rows
    .filter((n) => n && typeof n === 'object')
    .map((n) => ({
      id: String(n.id || n._id || ''),
      title: n.title || '',
      body: n.body || '',
      type: n.type || '',
      href: n.href || '',
      metadata: n.metadata && typeof n.metadata === 'object' ? n.metadata : {},
      read: !!n.read,
      at: Date.parse(n.created_at || n.createdAt || '') || 0
    }))
    .sort((a, b) => b.at - a.at)
  return { data: { items }, failed: !!(res && res.error) && !items.length }
}

// The model: Unread (with its count) then Earlier — `show` narrows to the
// unread ones or to this workspace's rows (the bell's Workspace tab rule).
export function notificationsAnswerModel(data, params, failed) {
  const el = this
  const root = (el && el.state && el.state.root) || {}
  const d = data && typeof data === 'object' ? data : {}
  const p = params && typeof params === 'object' ? params : {}
  const say = (key) => String(el.call('polyglot', key))
  if (failed) return { groups: [], note: 'workspace.notificationsAnswer.failed' }
  const show = ['all', 'unread', 'workspace'].includes(p.show) ? p.show : 'all'
  const limit = Math.max(1, Math.min(50, Math.floor(Number(p.limit)) || 12))
  let items = Array.isArray(d.items) ? d.items : []
  if (show === 'workspace') {
    items = items.filter((n) => el.call('isCurrentWorkspaceNotification', n, root) !== false)
  }
  const unread = items.filter((n) => !n.read)
  const earlier = show === 'unread' ? [] : items.filter((n) => n.read)
  const ICONS = {
    mention: 'chat',
    ticket_assign: 'check',
    calendar_invite: 'date',
    doc_share: 'document',
    system: 'settings',
    channel_message: 'chat',
    channel_missed_call: 'phone'
  }
  const when = (at) => {
    if (!at) return ''
    const d0 = new Date(at)
    const today = new Date()
    const sameDay = d0.toDateString() === today.toDateString()
    return String(
      el.call(
        'localizedDate',
        d0,
        sameDay ? { hour: '2-digit', minute: '2-digit' } : { month: 'short', day: 'numeric' }
      ) || ''
    )
  }
  const row = (n) => {
    const body = String(el.call('htmlToPlainText', n.body) || '').trim()
    const meta = n.metadata || {}
    const keep = {}
    for (const k of ['kind', 'calendarEventId', 'eventId', 'ticketObjectId', 'partyId']) {
      if (meta[k] != null) keep[k] = meta[k]
    }
    return el.call('assistantViewRow', {
      key: 'n-' + String(n.id || n.at).replace(/[^a-zA-Z0-9_-]/g, ''),
      title: n.title || body || say('workspace.notificationsAnswer.untitled'),
      meta: when(n.at),
      icon: ICONS[n.type] || 'bell',
      open: { call: 'notificationsAnswerOpen', arg: { href: n.href, type: n.type, metadata: keep } }
    })
  }
  const groups = []
  let budget = limit
  const unreadRows = unread.slice(0, budget).map(row)
  budget -= unreadRows.length
  if (unreadRows.length) {
    groups.push(
      el.call('assistantViewGroup', {
        key: 'unread',
        label: say('workspace.notificationsAnswer.group.unread').replace(
          '{count}',
          String(unread.length)
        ),
        rows: unreadRows
      })
    )
  }
  const earlierRows = earlier.slice(0, Math.max(0, budget)).map(row)
  if (earlierRows.length) {
    groups.push(
      el.call('assistantViewGroup', {
        key: 'earlier',
        label: say('workspace.notificationsAnswer.group.earlier'),
        rows: earlierRows
      })
    )
  }
  const note = groups.length
    ? ''
    : show === 'unread'
      ? 'workspace.notificationsAnswer.noUnread'
      : 'workspace.notificationsAnswer.empty'
  return {
    groups,
    note,
    more: { shown: unreadRows.length + earlierRows.length, total: unread.length + earlier.length }
  }
}

// Open a notification where the bell row opens it: its own href, else the one
// its type implies; a ticket or an event in its modal. `this` is the row.
export function notificationsAnswerOpen(target) {
  const t = target && typeof target === 'object' ? target : null
  if (!t || typeof this.call !== 'function') return false
  const notif = { href: t.href || '', type: t.type || '', metadata: t.metadata || {} }
  const href = this.call('notificationHrefOf', notif)
  if (!href) return false
  this.call('openNotificationTarget', { ...notif, href }, this)
  return true
}

// ── reminders ───────────────────────────────────────────────────────────────

// The reminders app's own read: the records plane's `reminders` collection in
// this workspace (reminderScopeWs), each row through rowToReminder.
export async function remindersAnswerLoad(el) {
  const host = el || this
  const root = (host && host.state && host.state.root) || {}
  const sdk = (host && host.sdk) || host.call('getSDK')
  if (!sdk || typeof sdk.execute !== 'function') return { data: { items: [] }, failed: true }
  const ws = host.call('reminderScopeWs', root)
  try {
    const res = await sdk.execute('workspaceProject.records', 'list', {
      collection: 'reminders',
      ...(ws ? { workspaceId: ws } : {}),
      limit: 100,
      order: 'updated_at'
    })
    const rows = Array.isArray(res) ? res : res && Array.isArray(res.data) ? res.data : []
    return { data: { items: rows.map((r) => host.call('rowToReminder', r)) } }
  } catch (_) {
    return { data: { items: [] }, failed: true }
  }
}

// The model: the app's own groups (reminderGroups) for the scope and filter
// asked — overdue, today, tomorrow, upcoming, no date, completed — with the
// app's due labels; an overdue row carries the red dot.
export function remindersAnswerModel(data, params, failed) {
  const el = this
  const root = (el && el.state && el.state.root) || {}
  const d = data && typeof data === 'object' ? data : {}
  const p = params && typeof params === 'object' ? params : {}
  const items = Array.isArray(d.items) ? d.items : []
  if (failed && !items.length) return { groups: [], note: 'workspace.reminders.loadFailed' }
  const filter = ['all', 'today', 'upcoming', 'completed'].includes(p.filter) ? p.filter : 'all'
  const scope = p.scope === 'shared' ? 'shared' : 'private'
  const limit = Math.max(1, Math.min(50, Math.floor(Number(p.limit)) || 12))
  const sections =
    el.call('reminderGroups', {
      reminders: items,
      remindersScope: scope,
      _remindersFilter: filter,
      currentUserEmail: root.currentUserEmail
    }) || []
  const groups = []
  let budget = limit
  let shown = 0
  let total = 0
  for (const g of sections) {
    const list = Array.isArray(g && g.items) ? g.items : []
    total += list.length
    const rows = list.slice(0, Math.max(0, budget)).map((r) =>
      el.call('assistantViewRow', {
        key: 'r-' + String(r.id || r.key || r.title).replace(/[^a-zA-Z0-9_-]/g, ''),
        title: r.title || String(el.call('polyglot', 'workspace.reminders.untitled')),
        meta: r.done ? '' : el.call('reminderDueLabel', r.dueAt) || '',
        icon: r.done ? 'checkmark' : 'clockOutline',
        bad: !!el.call('reminderIsOverdue', r.dueAt, r.done),
        open: { call: 'openAppPage', arg: '/reminders' }
      })
    )
    budget -= rows.length
    shown += rows.length
    if (rows.length) {
      groups.push(
        el.call('assistantViewGroup', {
          key: g.key,
          label: String(el.call('polyglot', g.label)),
          rows
        })
      )
    }
  }
  return {
    groups,
    note: groups.length ? '' : 'workspace.remindersAnswer.empty',
    more: { shown, total }
  }
}
